import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useForecastSession } from 'src/features/forecastSessions/useForecastSession';
import {
  getForecastSessionsQueryKey,
  getSessionCriteriaQueryKey,
  getSessionForecastDataQueryPrefix,
  getSessionProductsQueryKey,
} from 'src/keys';
import { useAddCriteriaToSession } from 'src/services/api/addCriteriaToOpenSession';
import { useDeleteCriteriaFromSession } from 'src/services/api/deleteCriteriaFromOpenSession';
import type { OpenSessionCriterion } from 'src/services/api/getOpenSessionCriteria';
import { useUpdateCriteriaFromSession } from 'src/services/api/updateCriteriaFromOpenSession';

function invalidateCriterionQueries(
  queryClient: QueryClient,
  sessionId: string,
  includeSessionsList = false,
) {
  queryClient.invalidateQueries({
    queryKey: getSessionCriteriaQueryKey(sessionId),
  });
  queryClient.invalidateQueries({
    queryKey: getSessionProductsQueryKey(sessionId),
  });
  queryClient.invalidateQueries({
    queryKey: getSessionForecastDataQueryPrefix(sessionId),
  });

  if (includeSessionsList) {
    queryClient.invalidateQueries({
      queryKey: getForecastSessionsQueryKey(),
    });
  }
}

export const useConfigurationCriterion = (criterion: OpenSessionCriterion) => {
  const { t } = useTranslation('configuration');
  const { activeSession } = useForecastSession();
  const queryClient = useQueryClient();
  const [isChecked, setIsChecked] = useState(criterion.apply);
  const sessionId = activeSession?.id;

  useEffect(() => {
    setIsChecked(criterion.apply);
  }, [criterion.apply]);

  const { mutate: addCriteriaToSession, isPending: isAddingCriteria } =
    useAddCriteriaToSession({
      onSuccess: () => {
        setIsChecked(true);
        if (sessionId) {
          invalidateCriterionQueries(queryClient, sessionId, true);
        }
      },
      onError: () => {
        toast.error(t('Error'), {
          description: t('Error while adding criteria to the session'),
          action: {
            label: t('Retry'),
            onClick: () => {
              if (!sessionId) {
                return;
              }

              addCriteriaToSession({
                sessionId,
                name: criterion.name,
                values: criterion.values ?? {},
              });
            },
          },
        });
      },
    });

  const { mutate: deleteCriteriaFromSession, isPending: isDeletingCriteria } =
    useDeleteCriteriaFromSession({
      onSuccess: () => {
        setIsChecked(false);
        if (sessionId) {
          invalidateCriterionQueries(queryClient, sessionId, true);
        }
      },
      onError: () => {
        toast.error(t('Error'), {
          description: t('Error while deleting criteria from the session'),
          action: {
            label: t('Retry'),
            onClick: () => {
              if (!sessionId) {
                return;
              }

              deleteCriteriaFromSession({
                sessionId,
                name: criterion.name,
              });
            },
          },
        });
      },
    });

  const { mutate: updateCriteriaFromSession, isError: updatingCriteriaError } =
    useUpdateCriteriaFromSession({
      onSuccess: () => {
        if (sessionId) {
          invalidateCriterionQueries(queryClient, sessionId);
        }
      },
      onError: () => {
        toast.error(t('Error'), {
          description: t('Error while updating criteria from the session'),
        });
        if (sessionId) {
          queryClient.invalidateQueries({
            queryKey: getSessionCriteriaQueryKey(sessionId),
          });
        }
      },
    });

  const handleChangeCriteria = (checked: boolean) => {
    if (!sessionId) {
      return;
    }

    if (checked) {
      addCriteriaToSession({
        sessionId,
        name: criterion.name,
        values: criterion.values ?? {},
      });
    } else {
      deleteCriteriaFromSession({
        sessionId,
        name: criterion.name,
      });
    }
  };

  const handleUpdateCriteria = (
    key: string,
    value: string,
    number: boolean,
  ) => {
    if (!criterion.values || value === '' || !sessionId) {
      return;
    }

    const values = {
      ...criterion.values,
    };
    values[key] = number ? +value : value;
    updateCriteriaFromSession({
      sessionId,
      name: criterion.name,
      values,
    });
  };

  return {
    isAddingCriteria,
    isDeletingCriteria,
    isChecked,
    handleChangeCriteria,
    handleUpdateCriteria,
    updatingCriteriaError,
  };
};
